// UiVersionSwitch — công tắc đổi giữa giao diện mới (v2) và giao diện cũ (v1).
// DÙNG CHUNG SaaS + Core (sếp Phú chốt 14/08 áp v2 cho cả hai).
//
// Đổi là ăn ngay, KHÔNG reload: chỉ set/bỏ attribute `data-ui` trên <html>.
// localStorage là nguồn sự thật để render; BE (preferences.ui_version) chỉ để đồng bộ
// sang thiết bị khác — và chỉ được phép SEED khi máy này CHƯA từng chọn, theo đúng
// luật hadLocalRef ở src/pages/me/sections/AppearanceSection.jsx:33-53
// (sự cố 2026-05-30: ưu tiên giá trị BE làm lật ngược lựa chọn ngay trên máy user).
//
// ⚠️ Từ 17/08 công tắc CHỈ CÒN CHIỀU v1 → v2 trong thực tế: biến thể `rail` ở đáy menu
// trái của v2 đã bị gỡ khỏi cả 3 host (Connect / Work / Core) theo lệnh sếp Phú — v2 là
// giao diện chính thức, không còn nút quay lui. Component vẫn giữ nguyên logic 2 chiều vì
// bản `floating` bên v1 dùng chung code này; muốn mở lại lối quay lui thì chỉ cần render
// lại nó ở đáy rail (xem RailBottomV2 của từng host).
//
// 2 biến thể còn dùng:
//   floating — pill nổi góc dưới-trái, dùng khi ĐANG Ở v1: v1 không có thanh trên cùng
//              nên đây là lối duy nhất bật lại v2. Đừng bỏ.
//   (không cờ) — inline, dùng khi nhúng vào thanh/menu có sẵn.

import { useEffect, useRef, useState } from 'react';
import { Sparkles, Undo2 } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { useAuth } from '@host/auth';
import { PRODUCT_NAME } from '../../lib/hostBrand';
import {
  UI_V1,
  UI_V2,
  readUiVersion,
  hasStoredUiVersion,
  applyUiVersion,
  setUiVersion,
  subscribeUiVersion,
} from '../../lib/uiVersion.js';

export default function UiVersionSwitch({ floating = false, rail = false, read = readUiVersion }) {
  const { t } = useLingui();
  const { user, setPreference } = useAuth();
  const [version, setVersion] = useState(() => read());

  // Máy này đã từng tự chọn chưa — chốt 1 lần lúc mount, trước khi /auth/me kịp về.
  const hadLocalRef = useRef(hasStoredUiVersion());

  // Nhận giá trị từ BE cho thiết bị MỚI (mở app lần đầu trên máy này).
  useEffect(() => {
    if (hadLocalRef.current) return;
    const fromBE = user?.preferences?.ui_version;
    if (fromBE !== UI_V1 && fromBE !== UI_V2) return;
    hadLocalRef.current = true;
    setVersion(fromBE);
    applyUiVersion(fromBE); // chỉ áp, KHÔNG ghi localStorage: đây là giá trị của BE
  }, [user?.preferences?.ui_version]);

  // Tab khác đổi → tab này đổi theo.
  useEffect(() => subscribeUiVersion(setVersion, read), [read]);

  const toV2 = version === UI_V1;

  const handleClick = () => {
    const next = toV2 ? UI_V2 : UI_V1;
    setVersion(setUiVersion(next));
    hadLocalRef.current = true;
    setPreference?.('ui_version', next); // debounce + keepalive flush ở AuthContext của host
  };

  const label = toV2 ? t`Giao diện mới` : t`Giao diện cũ`;
  const title = toV2
    ? t`Chuyển sang giao diện mới của ${PRODUCT_NAME}`
    : t`Quay về giao diện cũ — lựa chọn được ghi nhớ trên máy này`;

  const cls = rail ? 'v2-switch v2-switch--rail'
    : floating ? 'v2-switch v2-switch--floating'
      : 'v2-switch';

  return (
    <button
      type="button"
      onClick={handleClick}
      title={title}
      aria-label={title}
      className={cls}
    >
      {toV2 ? <Sparkles size={rail ? 15 : 13} /> : <Undo2 size={rail ? 15 : 13} />}
      <span>{label}</span>
    </button>
  );
}
